import { useCallback, useRef } from 'react'
import { TablePersistedState } from './types'
import { DEFAULT_SORT_STATE, DEFAULT_FILTER_STATE, DEFAULT_PAGE_SIZE } from './defaultsFromLegacy'

const SCHEMA_VERSION = 'v3'

const normalizePersistedField = (field?: string) => {
  if (!field) return ''
  if (field === 'product_name') return 'name'
  if (field === 'link_action') return 'link_actions'
  return field
}

const normalizePersistedState = (state: Partial<TablePersistedState>): Partial<TablePersistedState> => ({
  ...state,
  selectedColumns: (state.selectedColumns || [])
    .map((column) => ({
      ...column,
      field: normalizePersistedField(column.field),
    }))
    .filter((column) => Boolean(column.field)),
  sortState: state.sortState
    ? {
        ...state.sortState,
        field: normalizePersistedField(state.sortState.field),
      }
    : null,
  filterState: {
    globalSearch: state.filterState?.globalSearch || '',
    columnFilters: (state.filterState?.columnFilters || [])
      .map((filter) => ({
        ...filter,
        field: normalizePersistedField(filter.field),
      }))
      .filter((filter) => Boolean(filter.field)),
  },
})

interface PersistenceOptions {
  userId: string | number
  platformId: string | number
  channelId: string | number
}

export const useTablePersistence = ({ 
  userId, 
  platformId, 
  channelId
}: PersistenceOptions) => {
  const storageKey = `listing-product-table-v2:${SCHEMA_VERSION}:${userId}:${platformId}:${channelId}`
  const initializedRef = useRef(false)

  const loadPersistedState = useCallback((): Partial<TablePersistedState> | null => {
    try {
      const stored = localStorage.getItem(storageKey)
      if (!stored) return null

      const parsed = JSON.parse(stored) as TablePersistedState
      
      // Validate schema version
      if (parsed.version !== SCHEMA_VERSION) {
        console.warn('Schema version mismatch, using defaults')
        localStorage.removeItem(storageKey)
        return null
      }

      return normalizePersistedState(parsed)
    } catch (error) {
      console.error('Error loading persisted table state:', error)
      return null
    }
  }, [storageKey])

  const savePersistedState = useCallback(
    (state: Partial<TablePersistedState>) => {
      if (!initializedRef.current) return // Don't save during initial hydration

      try {
        const normalizedState = normalizePersistedState(state)
        const stateToSave: TablePersistedState = {
          version: SCHEMA_VERSION,
          selectedColumns: normalizedState.selectedColumns || [],
          ...(normalizedState.columnOrderCustomized !== undefined
            ? { columnOrderCustomized: normalizedState.columnOrderCustomized }
            : {}),
          sortState: normalizedState.sortState || null,
          filterState: normalizedState.filterState || { globalSearch: '', columnFilters: [] },
          pageSize: normalizedState.pageSize || 25,
        }

        localStorage.setItem(storageKey, JSON.stringify(stateToSave))
      } catch (error) {
        console.error('Error saving table state:', error)
      }
    },
    [storageKey]
  )

  const clearPersistedState = useCallback(() => {
    try {
      localStorage.removeItem(storageKey)
    } catch (error) {
      console.error('Error clearing table state:', error)
    }
  }, [storageKey])

  const getInitialState = useCallback(() => {
    const persisted = loadPersistedState()
    if (persisted && persisted.selectedColumns && persisted.selectedColumns.length > 0) {
      // Use persisted state if it has columns
      return persisted
    }
    // Return empty defaults - let useTableColumns set smart defaults based on platform type
    return {
      selectedColumns: [],
      columnOrderCustomized: false,
      sortState: DEFAULT_SORT_STATE,
      filterState: DEFAULT_FILTER_STATE,
      pageSize: DEFAULT_PAGE_SIZE,
    }
  }, [loadPersistedState])

  const markInitialized = useCallback(() => {
    initializedRef.current = true
  }, [])

  return {
    loadPersistedState,
    savePersistedState,
    clearPersistedState,
    getInitialState,
    markInitialized,
    isInitialized: () => initializedRef.current,
  }
}
